import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

/**
 * Check if the browser supports push notifications.
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Get the current notification permission state.
 */
export function getPushPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Register the service worker with explicit scope and wait for activation.
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  // If SW is installing/waiting, wait for it to activate
  if (reg.installing || reg.waiting) {
    await navigator.serviceWorker.ready;
  }
  return reg;
}

/**
 * Request notification permission from the user.
 * Only call this on user action (click).
 */
export async function requestNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.requestPermission();
}

/**
 * Get the VAPID public key from the server.
 */
async function getVapidPublicKey() {
  try {
    const res = await axios.get(`${API}/push/vapidPublicKey`);
    return res.data.vapidPublicKey;
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push and register with backend. Verifies backend storage.
 * Only call on user action.
 */
export async function subscribeToPush(token, marketingOptIn = false, language = null) {
  try {
    console.log('[Push] subscribeToPush started');
    const permission = await requestNotificationPermission();
    console.log('[Push] permission:', permission);
    if (permission !== 'granted') return { success: false, reason: 'permission_denied' };

    const vapidKey = await getVapidPublicKey();
    console.log('[Push] VAPID key:', vapidKey ? 'received' : 'MISSING');
    if (!vapidKey) return { success: false, reason: 'not_configured' };

    await registerServiceWorker();
    const registration = await navigator.serviceWorker.ready;
    console.log('[Push] SW ready');

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });
    console.log('[Push] Browser subscribed, endpoint:', subscription.endpoint.slice(0, 50));

    const subJSON = subscription.toJSON();
    let res;
    try {
      res = await axios.post(`${API}/push/subscribe`, {
        endpoint: subJSON.endpoint,
        keys: subJSON.keys,
        marketingOptIn,
        language
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      return { success: false, reason: 'subscribe_failed' };
    }

    console.log('[Push] Backend subscribe response:', res.status);

    // Verify backend actually stored the subscription
    const verify = await getServerPushStatus(token);
    console.log('[Push] Backend verification: count =', verify.count);
    if (verify.count === 0) {
      return { success: false, reason: 'not_stored_on_server' };
    }

    return { success: true, endpoint: subscription.endpoint };
  } catch (err) {
    console.error('[Push] subscribeToPush error:', err);
    return { success: false, reason: err.message || 'error' };
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(token) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await axios.post(`${API}/push/unsubscribe`, { endpoint }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    }
    return { success: true };
  } catch (err) {
    console.error('[Push] unsubscribe error:', err);
    return { success: false, reason: err.message };
  }
}

/**
 * Get the current browser push subscription endpoint (or null).
 */
export async function getBrowserSubscriptionEndpoint() {
  if (!isPushSupported()) return null;
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
    ]);
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? subscription.endpoint : null;
  } catch {
    return null;
  }
}

/**
 * Check if the user currently has an active browser push subscription.
 */
export async function isSubscribedToPush() {
  const endpoint = await getBrowserSubscriptionEndpoint();
  return !!endpoint;
}

/**
 * Query the backend for the user's stored push subscriptions.
 */
export async function getServerPushStatus(token) {
  try {
    const res = await axios.get(`${API}/push/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch {
    return { count: 0, subscriptions: [] };
  }
}

/**
 * Sync an existing browser push subscription to the backend.
 * Safe to call from useEffect (no permission prompt needed).
 */
/**
 * Get PushManager.supportedContentEncodings (for debugging encryption issues).
 */
export function getSupportedContentEncodings() {
  if (typeof PushManager !== 'undefined' && PushManager.supportedContentEncodings) {
    return PushManager.supportedContentEncodings;
  }
  return null;
}

/**
 * Get the service worker version (to detect stale SWs).
 */
export async function getSwVersion() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
    ]);
    if (!reg.active) return null;
    return new Promise((resolve) => {
      const ch = new MessageChannel();
      ch.port1.onmessage = (e) => resolve(e.data?.version || 'unknown');
      setTimeout(() => resolve('timeout'), 2000);
      reg.active.postMessage({ type: 'GET_VERSION' }, [ch.port2]);
    });
  } catch {
    return null;
  }
}

/**
 * Nuclear reset: unsubscribe browser, unregister SW, delete all server subs,
 * then re-register SW and resubscribe fresh. Use when push is broken.
 * Only call on user action.
 */
export async function nuclearReset(token) {
  const steps = [];
  try {
    // 1. Unsubscribe browser push
    if ('serviceWorker' in navigator) {
      try {
        const reg = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
        ]);
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await sub.unsubscribe();
          steps.push('browser_unsubscribed');
        }
      } catch {
        steps.push('browser_unsub_skipped');
      }

      // 2. Unregister all SWs
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        await reg.unregister();
      }
      steps.push('sw_unregistered_' + registrations.length);
    }

    // 3. Delete all server-side subscriptions
    if (token) {
      const res = await axios.post(`${API}/push/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      steps.push('server_deleted_' + (res.data.deleted || 0));
    }

    // 4. Small delay for SW unregister to complete
    await new Promise(r => setTimeout(r, 500));

    // 5. Re-register fresh SW
    await registerServiceWorker();
    await navigator.serviceWorker.ready;
    steps.push('sw_reregistered');

    // 6. Request permission (should already be granted)
    const perm = await requestNotificationPermission();
    if (perm !== 'granted') {
      return { success: false, reason: 'permission_denied', steps };
    }

    // 7. Get VAPID key and subscribe fresh
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) return { success: false, reason: 'not_configured', steps };

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });
    steps.push('browser_subscribed');

    // 8. Register with backend
    const subJSON = subscription.toJSON();
    try {
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJSON.endpoint,
        keys: subJSON.keys,
        marketingOptIn: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      return { success: false, reason: 'subscribe_failed', steps };
    }
    steps.push('server_subscribed');

    // 9. Verify
    const verify = await getServerPushStatus(token);
    if (verify.count === 0) {
      return { success: false, reason: 'not_stored_on_server', steps };
    }
    steps.push('verified_count_' + verify.count);

    return { success: true, endpoint: subscription.endpoint, steps };
  } catch (err) {
    console.error('[Push] nuclearReset error:', err);
    return { success: false, reason: err.message || 'error', steps };
  }
}

export async function syncPushSubscription(token, marketingOptIn = false) {
  if (!isPushSupported()) return { success: false, reason: 'unsupported' };
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
    ]);
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return { success: false, reason: 'no_subscription' };

    const subJSON = subscription.toJSON();
    try {
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJSON.endpoint,
        keys: subJSON.keys,
        marketingOptIn
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      return { success: false, reason: 'subscribe_failed' };
    }
    return { success: true };
  } catch {
    return { success: false, reason: 'error' };
  }
}
